// import components
import { Interval } from 'components/types';
import Header from '../Header';
import EmployeeGrid from './EmployeeGrid/EmployeeGrid';
import OpenRequestsGrid from './OpenRequestsGrid/OpenRequestsGrid';
import RosterViewByEmployeeStyles from './RosterViewByEmployee.styles';
import HeaderFilters, { FilterOption, RefProps } from './HeaderFilters';
import { Box } from '@mui/material';

// import hooks
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'hooks';

// import types
import { Employee } from './EmployeeGrid/types';
import { OpenRequestsRow } from './OpenRequestsGrid/types';
import { RosterViewByEmployeeProps } from './RosterViewByEmployee.types';

// import utils
import classNames from 'classnames';
import { values } from 'lodash';
import { getMonthInterval } from 'utils/date';
import { printContent } from 'utils/misc';
import { getRosterDays, prepareRosterForPrint } from '../utils';
import { ScrollbarBox } from '@sdb/planning-ui';

export default function RosterViewByEmployee(props: RosterViewByEmployeeProps) {
  const {
    portal,
    isAccordion,
    getData,
    selectedPlanUnits,
    lastSelectedPlanUnitsIds,
    onPlanUnitSelected,
    showOnlyPlanned,
    onShowOnlyPlanned,
    defaultInterval,
    onRosterViewChanged
  } = props;
  const [interval, setInterval] = useState(
    defaultInterval || getMonthInterval()
  );

  const holidays = useSelector((state) => state.holidays);
  const [rosterDays, setRosterDays] = useState(
    getRosterDays(interval, holidays)
  );
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filterOption, setFilterOption] = useState<FilterOption | null>(null);
  const [openRequestsRows, setOpenRequestsRows] = useState<OpenRequestsRow[]>(
    []
  );
  const rosterRef = useRef(document.createElement('div'));
  const headerFiltersRef = useRef<RefProps>({} as RefProps);

  const onIntervalChanged = (interval: Interval) => {
    setInterval(interval);
    setRosterDays(getRosterDays(interval, holidays));
  };

  const employeesByPlanned = useMemo(
    () =>
      showOnlyPlanned
        ? employees.filter((e) => values(e.days).flat().length)
        : employees,
    [showOnlyPlanned, employees]
  );

  const filteredEmployees = useMemo(
    () =>
      employeesByPlanned.filter((e) => {
        if (filterOption) {
          if (filterOption.type === 'name') {
            return e.nameInformal === filterOption.label;
          }
          if (filterOption.type === 'function') {
            return e.benefit.function?.name === filterOption.label;
          }
          if (filterOption.type === 'planunit') {
            return (
              e.benefit.costCenter?.name === filterOption.label ||
              e.plannedCostCenter.name === filterOption.label
            );
          }
        }
        return true;
      }),
    [employeesByPlanned, filterOption]
  );

  useEffect(() => {
    setEmployees([]);
    setOpenRequestsRows([]);
    if (selectedPlanUnits.length) {
      getData(interval, selectedPlanUnits).then(
        ({ employees, openRequestsRows }) => {
          setEmployees(employees);
          setOpenRequestsRows(openRequestsRows);
        }
      );
    }
  }, [interval, selectedPlanUnits]);

  useEffect(() => {
    headerFiltersRef.current.clearSearchFilter();
  }, [selectedPlanUnits]);

  return (
    <ScrollbarBox>
      <RosterViewByEmployeeStyles
        className={classNames({ 'is-accordion': isAccordion })}
        ref={rosterRef}
      >
        <Header
          lastSelectedPlanUnitsIds={lastSelectedPlanUnitsIds}
          onPlanUnitSelected={onPlanUnitSelected}
          onPrintRoster={() => {
            printContent(prepareRosterForPrint(rosterRef.current));
          }}
          interval={interval}
          onIntervalChanged={onIntervalChanged}
          rosterDays={rosterDays}
          calendarView="month"
          rosterView="employees"
          onRosterViewChanged={onRosterViewChanged}
        >
          <HeaderFilters
            employees={employeesByPlanned}
            showOnlyPlanned={showOnlyPlanned}
            onShowOnlyPlanned={onShowOnlyPlanned}
            onFilterOptionSelected={setFilterOption}
            ref={headerFiltersRef}
          />
        </Header>
        <div className="roster-body">
          <Box className="wrap-container">
            <EmployeeGrid
              employees={filteredEmployees}
              isAccordion={isAccordion}
              selectedPlanUnits={selectedPlanUnits}
              rosterDays={rosterDays}
              portal={portal}
              interval={interval}
            />
            <OpenRequestsGrid
              openRequestsRows={openRequestsRows}
              isAccordion={isAccordion}
              selectedPlanUnits={selectedPlanUnits}
              rosterDays={rosterDays}
              portal={portal}
            />
          </Box>
        </div>
      </RosterViewByEmployeeStyles>
    </ScrollbarBox>
  );
}
